import React, { MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import axios from 'axios';
import jwt from 'jwt-simple';
import Login from '../Components/LoginComponent';
import { RootState } from '../Redux/reducers/index';

const loginAPI = '/api/v2/core/auth/login';

const checkAuth = (dispatch: ({ type, payload }: { type: string, payload: boolean })=> void): boolean => {
  if (sessionStorage.getItem('authToken')) {
    dispatch({ type: 'LOGIN', payload: true });
  }
  return true;
};

export const adminAccess = (permission: string[]): boolean => {
  if (
    permission.indexOf('role') !== -1
    || permission.indexOf('token') !== -1
    || permission.indexOf('transaction') !== -1
  ) {
    return true;
  }
  return false;
};

export const senderAccess = (permission: string[]): boolean => {
  if ((permission.indexOf('sender') !== -1 || permission.indexOf('send_message') !== -1)) {
    return true;
  }
  return false;
};

export const hasAccess = (path: string, permissions: string[]): boolean => {
  switch (path) {
    case '/':
      return true;
    case '/login':
      return true;
    case '/dashboard':
      return true;
    case '/sender':
      return senderAccess(permissions);
    case '/users':
      return permissions.indexOf('user') !== -1;
    case '/channels':
      return permissions.indexOf('channel') !== -1;
    case '/files':
      return permissions.indexOf('file') !== -1 && permissions.indexOf('channel') !== -1;
    case '/admin':
      return adminAccess(permissions);
    case '/styles':
      return permissions.indexOf('styles') !== -1;
    case '/mobileActivation':
      return adminAccess(permissions);
    default:
      return false;
  }
};

const LoginContainer = (): JSX.Element => {
  const [statusMessage, setStatusMessage] = React.useState('');
  const [statusType, setStatusType] = React.useState('');
  const [userInputValue, setUserInputValue] = React.useState('');
  const [passInputValue, setPassInputValue] = React.useState('');

  const { loggedIn } = useSelector((state: RootState) => state.login);
  const loginDispatch = useDispatch();

  React.useEffect(() => {
    checkAuth(loginDispatch);
  }, []);

  const onUserInput = (e: React.ChangeEvent<HTMLInputElement>): boolean => {
    e.preventDefault();
    setUserInputValue(e.target.value);
    return true;
  };

  const onPassInput = (e: React.ChangeEvent<HTMLInputElement>): boolean => {
    e.preventDefault();
    const name = e.target.value.replace(/\s/g, '');
    setPassInputValue(name);
    return true;
  };

  const handleLogin = async (e:  React.MouseEvent<HTMLButtonElement, MouseEvent> | any) => { //eslint-disable-line
    if (e !== undefined) e.preventDefault(); // eslint-disable-line
    let r,
      trimmedName = userInputValue.replace(/^[ \t]+/, '');
    trimmedName = trimmedName.replace(/[ \t]+$/, '');
    const payload = { email: trimmedName, password: passInputValue };
    try {
      r = await axios.post(loginAPI, payload);
      if (r.status === 200) {
        const res = r;
        sessionStorage.setItem('authToken', res.data.token);
        const secret = process.env.REACT_APP_JWT_SECRET;
        const decoded = secret ? jwt.decode(res.data.token, secret) as {
          channels: string[], permissions: string[], userId: string, role: string
        } : null;
        if (decoded !== null) {
          sessionStorage.setItem('authorizedUser', decoded.userId);
          sessionStorage.setItem('userRole', res.data.role);
          sessionStorage.setItem('userRoleName', res.data.roleName);
          sessionStorage.setItem('channels', JSON.stringify(decoded.channels));
          sessionStorage.setItem('permissions', JSON.stringify(decoded.permissions));
        } else {
          sessionStorage.setItem('channels', '');
          sessionStorage.setItem('permissions', '');
        }
        loginDispatch({ type: 'LOGIN', payload: true });
        return true;
      }
      setStatusMessage('Error logging in');
      setStatusType('error');
      return false;
    } catch (err) {
      setStatusMessage('Please enter a valid username or password');
      setStatusType('error');
      return false;
    }
  };

  const validateInput = (): boolean => {
    if (userInputValue.length <= 1 || passInputValue.length <= 1) {
      return true;
    }
    if (userInputValue.includes('@')) {
      return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInputValue);
    }
    return false;
  };

  if (loggedIn === false) {
    return (
      <Login
        title="LOGIN"
        subtitle="To get started, enter your credentials below"
        isAuthenticated={loggedIn}
        userInputValue={userInputValue}
        passInputValue={passInputValue}
        onUserInput={(e: React.ChangeEvent<HTMLInputElement>): boolean => onUserInput(e)}
        onPassInput={(e: React.ChangeEvent<HTMLInputElement>): boolean => onPassInput(e)}
        validateLogin={validateInput}
        handleLogin={handleLogin}
        statusMessage={statusMessage}
        statusType={statusType}
      />
    );
  }
  return <Redirect to="/dashboard" />;
};

export default LoginContainer;
